import { useCallback, useEffect, useState } from 'react';
import type { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';

export interface FigureSearchState {
  results: IFigureDto[];
  isSearchMode: boolean;
  totalCount: number;
  collectionId: number | null;
  hasMore: boolean;
}

interface UseFigureSearchStateParams {
  customerId: number;
  activeCollectionId: number | null | undefined;
  isDefaultActive: boolean;
}

interface UseFigureSearchStateResult {
  searchState: FigureSearchState;
  setSearchState: React.Dispatch<React.SetStateAction<FigureSearchState>>;
  seriesId: number | null;
  setSeriesId: React.Dispatch<React.SetStateAction<number | null>>;
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
  handleSearchResults: (results: IFigureDto[], totalCount: number, hasMore: boolean) => void;
  handleSearchModeChange: (isSearching: boolean) => void;
}

export function useFigureSearchState({ customerId, activeCollectionId, isDefaultActive }: UseFigureSearchStateParams): UseFigureSearchStateResult {
  const [searchState, setSearchState] = useState<FigureSearchState>({
    results: [],
    isSearchMode: false,
    totalCount: 0,
    collectionId: null,
    hasMore: false,
  });

  const [seriesId, setSeriesId] = useState<number | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const figureHasTags = useCallback((figure: IFigureDto, tags: string[]): boolean => {
    if (tags.length === 0) return true;

    const bfsTags: string[] = (figure.metadata || '')
      .split('\n')
      .filter(Boolean)
      .map((tag) => tag.trim().toLowerCase());

    const customerTags: string[] = (figure.customerMetadata || '')
      .split('#')
      .filter(Boolean)
      .map((tagStr) => {
        const parts = tagStr.trim().split(':');
        return parts[0].trim().toLowerCase();
      });

    const allFigureTags = [...bfsTags, ...customerTags];

    return tags.some((selectedTag) => allFigureTags.includes(selectedTag.toLowerCase()));
  }, []);

  const handleSearchResults = useCallback(
    (results: IFigureDto[], totalCount: number, hasMore: boolean) => {
      const currentCollectionId = activeCollectionId ?? null;
      if (currentCollectionId === null) {
        return;
      }

      let filteredResults = seriesId != null ? results.filter((r) => (r.seriesId ?? 0) === seriesId) : results;

      if (selectedTags.length > 0) {
        filteredResults = filteredResults.filter((r) => figureHasTags(r, selectedTags));
      }

      const filteredTotal = filteredResults.length;

      setSearchState((prev) => ({
        ...prev,
        results: filteredResults,
        totalCount: filteredTotal,
        hasMore,
        collectionId: currentCollectionId,
      }));
    },
    [activeCollectionId, seriesId, selectedTags, figureHasTags],
  );

  const handleSearchModeChange = useCallback(
    (isSearching: boolean) => {
      const currentCollectionId = activeCollectionId ?? null;
      if (currentCollectionId === null) {
        return;
      }

      setSearchState((prev) => ({
        ...prev,
        isSearchMode: isSearching,
        collectionId: currentCollectionId,
      }));
    },
    [activeCollectionId],
  );

  // Cross-collection tags-only search when default collection is active
  useEffect(() => {
    if (!isDefaultActive) return;

    const run = async () => {
      if (selectedTags.length === 0) {
        handleSearchModeChange(false);
        setSearchState((prev) => ({ ...prev, results: [], totalCount: 0, hasMore: false }));
        return;
      }

      try {
        handleSearchModeChange(true);
        const result = await FigurService.searchByTags(selectedTags, 1, 100, Number(customerId));
        handleSearchResults(result.data, result.totalCount, result.hasMore);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('searchByTags failed', e);
      }
    };

    void run();
  }, [isDefaultActive, selectedTags, customerId, handleSearchModeChange, handleSearchResults]);

  return {
    searchState,
    setSearchState,
    seriesId,
    setSeriesId,
    selectedTags,
    setSelectedTags,
    handleSearchResults,
    handleSearchModeChange,
  };
}
